// Dashboard data formatter — pure functions that transform API responses into
// display-ready stat card data.
//
// DESIGN DECISION: This is deliberately NOT a React hook. It takes API data in,
// returns plain objects out. Benefits:
//  - Testable without React test infrastructure (just call the function)
//  - Composable — called from both screen and print layouts with the same data
//  - View component only handles rendering, formatter only handles data shaping
//
// Sport branching: soccer sessions get seven tiles (current behaviour);
// American Football + Canadian Football sessions get six tiles sourced from
// `americanFootballTopLists` + `summaryMetrics`. The AF top lists arrive
// pre-sorted from the backend so we don't re-derive top-3 via `deriveTop3`.
import {
  isGridironSport,
  type AmericanFootballTeamSummaryTopLists,
  type AmericanFootballTopListSection,
  type PlayerStatsWithId,
  type ReportingPlayer,
  type TeamSessionSummaryResponse
} from '@frontend/core/lib/domains/reporting/types'
import { formatStatValue } from '../../lib/format-cell'
import type { ReportingSportConfig } from '../../lib/sport-config'
import { deriveTop3, sumMetric } from '../../lib/top-performers'

// --- Types ---

export interface StatCardData {
  label: string
  value: string | number
  topPerformers: { displayName: string; value: string | number }[]
}

// --- Helpers ---

/** Safely extract a numeric value from a potentially nested metric object. */
export function getMetricValue(metric: unknown): number | null {
  if (metric == null) return null
  if (typeof metric === 'number') return metric
  if (typeof metric === 'object' && 'value' in metric) {
    const val = (metric as { value: unknown }).value
    return typeof val === 'number' ? val : null
  }
  return null
}

function formatTop3(
  playerStats: PlayerStatsWithId[],
  players: ReportingPlayer[],
  metricKey: string,
  formatValue: (v: number) => string | number = (v) => v
): { displayName: string; value: string | number }[] {
  return deriveTop3(playerStats, players, metricKey).map((e) => ({
    displayName: e.player.displayName,
    value: formatValue(e.playerValue)
  }))
}

// Format pre-sorted top entries from an AF top-list section. Unlike
// soccer, the reporting-api already ranks these server-side, so no
// re-derivation — we just read `section.topPlayers`.
function formatTopPlayers(
  section: AmericanFootballTopListSection | undefined,
  formatValue: (v: number) => string | number = (v) => v
): { displayName: string; value: string | number }[] {
  if (!section || !Array.isArray(section.topPlayers)) return []
  return section.topPlayers.slice(0, 3).map((e) => ({
    displayName: e.player.displayName,
    value: formatValue(e.playerValue)
  }))
}

// --- Main builder ---

/**
 * Build all stat card data for the dashboard from raw API responses.
 * Pure function — no React dependencies. Empty cards (no team value and
 * no top-performer entries) are dropped so the grid only shows metrics
 * that actually have data for this session.
 */
export function buildDashboardCards(
  sessionSummary: TeamSessionSummaryResponse | undefined,
  playerStats: PlayerStatsWithId[],
  players: ReportingPlayer[],
  config: ReportingSportConfig
): StatCardData[] {
  if (!sessionSummary) return []
  const sport = config.sport
  const raw = isGridironSport(sport)
    ? buildAmericanFootballDashboardCards(sessionSummary, config)
    : buildSoccerDashboardCards(sessionSummary, playerStats, players, config)
  return raw.filter(isNonEmptyCard)
}

// A card is empty when the team total is missing/zero AND none of the
// top-performer entries carry a value. Keeps cards where either the
// team number or any player has something to show.
function isNonEmptyCard(card: StatCardData): boolean {
  const teamHasValue = !isBlankValue(card.value)
  if (teamHasValue) return true
  const topHasValue = card.topPerformers.some(
    (entry) => !isBlankValue(entry.value)
  )
  return topHasValue
}

function isBlankValue(value: string | number): boolean {
  if (typeof value === 'number') return value === 0
  // Strip thousands separators / trailing decimals ("0", "0.0", "0,0",
  // "0.00", "-") so formatted strings still resolve as blank.
  if (value === '-' || value === '') return true
  const numeric = Number(value.replace(/,/g, ''))
  return !Number.isNaN(numeric) && numeric === 0
}

function buildSoccerDashboardCards(
  sessionSummary: TeamSessionSummaryResponse,
  playerStats: PlayerStatsWithId[],
  players: ReportingPlayer[],
  config: ReportingSportConfig
): StatCardData[] {
  const fmtDistance = (meters: number) =>
    formatStatValue(meters, 'distance', config.unitSystem)
  const fmtSpeed = (mps: number) =>
    formatStatValue(mps, 'speed', config.unitSystem)
  const fmtCount = (v: number) => formatStatValue(v, 'count', config.unitSystem)
  const m = sessionSummary.summaryMetrics

  const distanceValue = getMetricValue(m.totalDistance_m)
  const speedValue = getMetricValue(m.avgMaxSpeed_mps)
  const accelValue = getMetricValue(m.highIntensityAccelCount)

  return [
    {
      label: `Total Distance (${config.distanceUnit})`,
      value: distanceValue != null ? fmtDistance(distanceValue) : '-',
      topPerformers: formatTop3(
        playerStats,
        players,
        'totalDistance_m',
        fmtDistance
      )
    },
    {
      label: `Avg. Max Speed (${config.speedUnit})`,
      value: speedValue != null ? fmtSpeed(speedValue) : '-',
      topPerformers: formatTop3(playerStats, players, 'maxSpeed_mps', fmtSpeed)
    },
    {
      label: 'Acceleration Count',
      value: accelValue != null ? fmtCount(accelValue) : '-',
      topPerformers: formatTop3(
        playerStats,
        players,
        'highIntensityAccelCount',
        fmtCount
      )
    },
    {
      label: 'Involvements',
      value: fmtCount(sumMetric(playerStats, 'involvements')),
      topPerformers: formatTop3(playerStats, players, 'involvements', fmtCount)
    },
    {
      label: 'Carries',
      value: fmtCount(sumMetric(playerStats, 'carries')),
      topPerformers: formatTop3(playerStats, players, 'carries', fmtCount)
    },
    {
      label: 'Completed Passes',
      value: fmtCount(sumMetric(playerStats, 'passesMade')),
      topPerformers: formatTop3(playerStats, players, 'passesMade', fmtCount)
    },
    {
      label: 'Shots',
      value: fmtCount(sumMetric(playerStats, 'shotsAttempted')),
      topPerformers: formatTop3(
        playerStats,
        players,
        'shotsAttempted',
        fmtCount
      )
    }
  ]
}

// AF/CFL six-tile dashboard. Every tile pulls both its team total and
// its ranked player list from `americanFootballTopLists.<metric>` —
// `summaryMetrics` provides redundant totals for the same metrics but
// the top-list section carries the canonical number for each card.
function buildAmericanFootballDashboardCards(
  sessionSummary: TeamSessionSummaryResponse,
  config: ReportingSportConfig
): StatCardData[] {
  const topLists: Partial<AmericanFootballTeamSummaryTopLists> =
    sessionSummary.americanFootballTopLists ?? {}

  const fmtDistance = (m: number) =>
    formatStatValue(m, 'distance', config.unitSystem)
  const fmtSpeed = (mps: number) =>
    formatStatValue(mps, 'speed', config.unitSystem)
  const fmtCount = (v: number) => formatStatValue(v, 'count', config.unitSystem)
  const fmt1dp = (v: number) => formatStatValue(v, undefined, config.unitSystem)

  const formatTeam = (
    section: AmericanFootballTopListSection | undefined,
    formatValue: (v: number) => string | number
  ): string | number =>
    section?.teamValue != null ? formatValue(section.teamValue) : '-'

  return [
    {
      label: `Total Distance (${config.distanceUnit})`,
      value: formatTeam(topLists.totalDistance_m, fmtDistance),
      topPerformers: formatTopPlayers(topLists.totalDistance_m, fmtDistance)
    },
    {
      label: `Avg. Max Speed (${config.speedUnit})`,
      value: formatTeam(topLists.avgMaxSpeed_mps, fmtSpeed),
      topPerformers: formatTopPlayers(topLists.avgMaxSpeed_mps, fmtSpeed)
    },
    {
      label: 'Sprint Count',
      value: formatTeam(topLists.sprintCount, fmtCount),
      topPerformers: formatTopPlayers(topLists.sprintCount, fmtCount)
    },
    {
      label: `Ball Velocity (${config.speedUnit})`,
      value: formatTeam(topLists.initialReleaseVelocityAvg_mps, fmtSpeed),
      topPerformers: formatTopPlayers(
        topLists.initialReleaseVelocityAvg_mps,
        fmtSpeed
      )
    },
    {
      label: 'Completion %',
      value: formatTeam(topLists.passCompletionPct, fmt1dp),
      topPerformers: formatTopPlayers(topLists.passCompletionPct, fmt1dp)
    },
    {
      label: 'Time to Throw (s)',
      value: formatTeam(topLists.timeToThrowAvg_s, fmt1dp),
      topPerformers: formatTopPlayers(topLists.timeToThrowAvg_s, fmt1dp)
    }
  ]
}
